import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';

export interface BroadcastMessage {
  type: string;
  data?: any;
  source: string;
  timestamp: number;
}

/**
 * BroadcastChannel Service
 * Synchronizes token updates, modal states, and auth status across browser tabs/windows
 * Uses BroadcastChannel API for cross-tab communication
 */
@Injectable({
  providedIn: 'root'
})
export class BroadcastChannelService {
  private channel: BroadcastChannel | null = null;
  private readonly CHANNEL_NAME = 'abuse-automation-auth';

  // Message types
  readonly MESSAGE_TYPES = {
    TOKEN_REFRESHED: 'TOKEN_REFRESHED',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    ACCOUNT_DISABLED: 'ACCOUNT_DISABLED',
    SESSION_REVOKED: 'SESSION_REVOKED',
    LOGOUT: 'LOGOUT',
    LOGIN: 'LOGIN',
  } as const;

  private messageSubject = new Subject<BroadcastMessage>();
  public messages$: Observable<BroadcastMessage> = this.messageSubject.asObservable();

  constructor() {
    // Check if BroadcastChannel is supported
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.channel = new BroadcastChannel(this.CHANNEL_NAME);
        
        this.channel.onmessage = (event) => {
          // Don't process our own messages (they're already handled locally)
          if (event.data.source !== 'self') {
            this.messageSubject.next(event.data);
          }
        };

        this.channel.onmessageerror = (error) => {
          console.error('BroadcastChannel message error:', error);
        };
      } catch (error) {
        console.warn('BroadcastChannel not available:', error);
        // Continue without BroadcastChannel - app will work but without cross-tab sync
      }
    } else {
      console.warn('BroadcastChannel API not supported in this browser');
    }
  }

  /**
   * Post a message to all other tabs/windows
   */
  postMessage(type: string, data?: any): void {
    if (this.channel) {
      try {
        this.channel.postMessage({
          type,
          data,
          source: 'self',
          timestamp: Date.now()
        });
      } catch (error) {
        console.error('Error posting BroadcastChannel message:', error);
      }
    }
  }

  /**
   * Close the BroadcastChannel connection
   */
  close(): void {
    if (this.channel) {
      try {
        this.channel.close();
        this.channel = null;
      } catch (error) {
        console.error('Error closing BroadcastChannel:', error);
      }
    }
  }

  /**
   * Check if BroadcastChannel is supported and available
   */
  isSupported(): boolean {
    return this.channel !== null;
  }
}
